<template>
  <div>
    <a-modal
      :open="open"
      centered
      :title="`Configura ${CompanyName} AI`"
      width="min(900px, 80vw)"
      :bodyStyle="{ height: '510px' }"
      @cancel="handleCancel"
    >
      <section class="container">
        <div class="left">
          <IconIA />
          <h2>{{ CompanyName }} AI</h2>
          <div v-if="isEdit">
            <span class="connected">
              <CheckCircleTwoTone style="font-size: 20px" two-tone-color="#48aa1e" />
              Conectado
            </span>

            <p class="info">
              ¡Potencia la gestión de tus tickets con inteligencia artificial! Obtén conclusiones de
              tus conversaciones para una gestión de tickets más eficiente.
            </p>
          </div>

          <img
            v-else
            style="max-width: 120%"
            :alt="`${CompanyName} AI popover example`"
            :src="aiExampleImage"
          />

          <p class="info">
            💡 ¿Tienes dudas sobre cómo conectar tu API Key? Visita nuestro
            <a-button type="link" class="text-button"> Centro de ayuda. </a-button>
          </p>
        </div>

        <a-divider type="vertical" class="divider" />

        <div class="right">
          <a-tabs v-if="isEdit" v-model:activeKey="current">
            <a-tabs-pane
              v-for="(step, index) in steps.toReversed()"
              :key="index"
              :tab="step.title"
            />
          </a-tabs>
          <a-steps v-else class="steps" progress-dot :current="current" :items="steps" />

          <div class="description" v-if="isEdit ? current === 1 : current === 0">
            <h3>Descripción general</h3>
            <p>
              Utiliza inteligencia artificial para generar conclusiones eficientes de tus chats y
              obtener una visión clara de todo lo conversado con el contacto.
              <strong>Ahorra tiempo, y toma decisiones más rápidas e informadas.</strong>

              <br />
              <br />

              Recuerda que:
            </p>

            <ul>
              <!-- <li>Se te cobrará un fee mensual de $5 por el uso de Score GPT.</li> -->
              <li>
                Debes contar con una cuenta creada en
                <a href="https://openai.com/" target="_blank" rel="noopener noreferrer">OpenAI.</a>
              </li>
              <li>Te recomendamos tener una cuenta Plus para una experiencia óptima.</li>
              <li>Recuerda recargar tu saldo en Openai para mantener activo el servicio.</li>
            </ul>

            <h3 style="margin-bottom: 16px">Acciones</h3>
            <ul class="actions">
              <li style="margin-bottom: 16px">
                Administradores pueden configurar si los agentes pueden o no también generar estas
                conclusiones al finalizar su gestión en Workspace. Puedes realizar la configuración
                desde el editor de tus campañas.
              </li>
              <li>
                Administradores pueden generar conclusiones inteligentes desde el historial de cada
                ticket. Estas conclusiones son por la conversación que se seleccione.
              </li>
            </ul>
          </div>

          <div v-else>
            <a-form layout="vertical">
              <a-form-item name="api-key">
                <template #label>
                  <span>
                    <strong> API Key </strong>
                    <a-tooltip>
                      <template #title>Ingresar el Api Key a registrar</template>
                      <InfoCircleOutlined />
                    </a-tooltip>
                  </span>

                  <span class="help-text">
                    Obtén tu API Key desde
                    <a href="http://platform.openai.com" target="_blank" rel="noopener noreferrer">
                      OpenAI Platform
                    </a>
                  </span>
                </template>
                <a-input v-model:value="apiKey" placeholder="Ingresar Api Key" />
              </a-form-item>
            </a-form>
          </div>
        </div>
      </section>

      <template #footer>
        <footer class="footer">
          <div>
            <a-button v-if="isEdit" ghost danger @click="confirmationModalOpen = true"
              >Desconectar</a-button
            >
          </div>
          <div class="actions">
            <a-button @click="handleCancel">Cancel</a-button>
            <div class="payment" v-if="showPaymentButton()">
              <a-button
                v-if="isEdit"
                type="link"
                class="link"
                href="https://openai.com/"
                target="_blank"
              >
                Consultar saldo disponible
                <IconRedirect />
              </a-button>
              <PayUPaymentButton
                v-else
                :amount="50"
                name="Activar y Pagar"
                :type="ProductType.Summary"
                :apiKey="apiKey"
                :disabled="isApiKeyEmpty"
                :title="`Realiza el pago mensual por ${CompanyName} AI`"
              />
            </div>
            <div v-else>
              <a-button
                v-if="primaryText === 'Consultar Saldo Disponible'"
                type="link"
                class="link"
                href="https://openai.com/"
                target="_blank"
              >
                {{ primaryText }}
                <IconRedirect />
              </a-button>
              <a-button
                v-else
                type="primary"
                @click="handlePrimary"
                :loading="loading"
                :disabled="disabled"
              >
                {{ primaryText }}
              </a-button>
            </div>
          </div>
        </footer>
      </template>
    </a-modal>

    <a-modal v-model:open="confirmationModalOpen" centered width="450px">
      <div class="confirmation">
        <IconIA />

        <h2 style="margin: 25px 0 12px">¿Estás seguro de desactivar {{ CompanyName }} AI?</h2>

        <div class="info" style="text-align: left">
          <p>Sucederán las siguientes acciones:</p>

          <ul>
            <!-- <li v-if="billingType === BillingType.Postpaid">
              Este mes será el último en el que se te cobrará el fee mensual por Score AI al
              finalizar tu fecha de facturación.
            </li>
            <li v-else>No se te cobrará por Score AI en tu siguiente ciclo de facturación.</li> -->
            <li>
              Todas las configuraciones realizadas sobre {{ CompanyName }} IA a nivel de empresa y
              campaña desaparecerán.
            </li>
            <li>Podrás volverlo a activar cuando lo consideres necesario.</li>
          </ul>
        </div>

        <a-form layout="vertical">
          <a-form-item required>
            <template #label>
              <span>Escribe la palabra <strong> "Desactivar"</strong></span>
            </template>

            <a-input v-model:value="confirmation" placeholder="Escribe aquí..." />
          </a-form-item>
        </a-form>
      </div>

      <template #footer>
        <footer class="confirmation footer">
          <a-button
            type="primary"
            :disabled="confirmation !== 'Desactivar'"
            @click="handleDisconnect"
          >
            Desactivar {{ CompanyName }} AI
          </a-button>
        </footer>
      </template>
    </a-modal>
  </div>

  <GalacticSuccessModal
    v-model:open="successOpen"
    :title="`¡${CompanyName} AI se ha activado! 🚀 🚀`"
    subtitle="Ya puedes comenzar este viaje intergaláctico"
    description="Despliega tu poder y obtén conclusiones inteligentes sobre las conversaciones que se tiene con tus contactos. ¡Tendrás un mejor control sobre tu galaxia!"
    okText="Aceptar"
    @closeModal="closeGalacticModal"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, watchEffect } from "vue";
import { message } from "ant-design-vue";

import { BillingType } from "@/@types/settings/billing";
import { ResponseError } from "@/app/shared/utils/httpManager";
import { CheckCircleTwoTone, InfoCircleOutlined } from "@ant-design/icons-vue";
import { integrationManager } from "@/app/integrations/services/IntegrationManager";
import SlotManager from "@/app/settings/services/slots/SlotManager";
import GalacticSuccessModal from "@/app/shared/components/organisms/GalacticSuccessModal.vue";
import PayUPaymentButton from "@/app/shared/components/buttons/PayUPaymentButton.vue";
import { ProductType } from "@/@types/settings/billing";
import EventBus from "@/app/shared/utils/eventBus";
import IconIA from "@/app/common/components/atoms/IconIA.vue";
import IconRedirect from "@/app/common/components/atoms/IconRedirect.vue";
import { CompanyName } from "@/app/shared/utils/constants";

import aiExampleImage from "@/app/integrations/assets/ai-example.png";

const steps = [{ title: "Descripción general" }, { title: "API Key" }];

const props = defineProps<{
  open: boolean;
  billingType: BillingType;
  isEdit: boolean;
  gptToken: string;
}>();

const manager = new SlotManager();
const summaryType = ref("");
const current = ref(0);
const apiKey = ref(props.isEdit ? props.gptToken : "");
const successOpen = ref(false);
const loading = ref(false);
const disabled = ref(false);
const confirmationModalOpen = ref(false);
const confirmation = ref("");

const emit = defineEmits<{ "update:open": [open: boolean] }>();

const isPrepaid = computed(() => {
  if (summaryType.value === "Prepago") return true;
  return false;
});

const primaryText = computed(() => {
  if (props.isEdit) {
    if (current.value === 0) return "Guardar API Key";
    return "Consultar Saldo Disponible";
  }

  //if (current.value === steps.length) return "Activar";
  if (current.value === 1) return "Activar";

  return "Continuar";
});

watchEffect(() => {
  if (current.value === 1) {
    disabled.value = apiKey.value === "";
  }
});

const fetchResume = async () => {
  const response = await manager.getInfoSlots();
  summaryType.value = response.summary_type_billing;
};

onMounted(() => {
  fetchResume();
});

const handleCancel = () => {
  emit("update:open", false);
};

const primaryAction = async () => {
  if (!isPrepaid.value || props.isEdit) {
    loading.value = true;

    try {
      await integrationManager.updateCustomerSetting(apiKey.value, true);
      successOpen.value = true;
      if (props.isEdit) {
        message.success("La actualización fue realizada con éxito");
      } else {
        message.success("El pago de IA fue realizado con éxito");
      }
    } catch (error) {
      let errorMessage = "Token Inválido";
      if (error instanceof ResponseError) errorMessage = error.getErrorMessage();
      message.error(errorMessage);
    }
    loading.value = false;
  }
};

const isApiKeyEmpty = computed(() => apiKey.value === "");

const handlePrimary = async () => {
  if (current.value === 0 && !props.isEdit) {
    current.value++;
    return;
  }

  await primaryAction();
};

const handleDisconnect = async () => {
  loading.value = true;
  confirmationModalOpen.value = false;
  try {
    await integrationManager.updateCustomerSetting(apiKey.value, false);
    emit("update:open", false);
    message.success(`Se ha desactivado ${CompanyName} AI con éxito`);
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";
    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();
    message.error(errorMessage);
  }
  loading.value = true;
};

const showPaymentButton = () => {
  if (current.value === 1 && isPrepaid.value) return true;
  return false;
};

const closeGalacticModal = (message: boolean) => {
  emit("update:open", message);
};

EventBus.on("showSuccessModal", () => {
  successOpen.value = true;
  emit("update:open", false);
});

watch(current, (newValue: number) => {
  console.log(newValue);
  showPaymentButton();
});

watch(
  () => props.isEdit,
  (newValue) => {
    console.log("isEdit changed:", newValue);
  }
);
</script>

<style scoped lang="sass">
img
  max-width: 100%

h2
  margin: 0
  font-size: 16px

h3
  margin: 0
  color: $text-primary
  font-weight: 600
  font-size: 14px

p
  margin: 0

ul
  padding-left: 1.5em

.text-button
  padding: 0
  height: 22px

.container
  display: flex
  height: 100%

.footer
  display: flex
  justify-content: space-between
  .actions
    display: flex
    gap: 10px
    .link
      display: flex
      border: 1px solid $primary
      gap: 5px
      align-items: center
      svg
        height: 16px
        width: 16px

.divider
  background-color: rgba(0, 0, 0, 0.15)
  height: auto
  margin: 0 25px

.left
  display: flex
  flex-direction: column
  align-items: center
  min-width: 220px
  width: 220px

.info
  color: $gray-8
  text-align: center
  margin: auto 0 0

.right
  width: 100%

.steps
  margin-bottom: 24px

.description
  color: $gray-8

.actions
  list-style: none

  li::before
    content: '\2713'
    float: left
    margin-left: -1.5em

:deep(.ant-form-item-label)
  width: 100%

:deep(label[for="form_item_api-key"])
  display: flex
  justify-content: space-between
  width: 100%

  .help-text
    color: $text-secondary

.connected
  color: #48aa1e
  display: flex
  align-items: center
  justify-content: center
  gap: 5px
  margin: 8px 0

.confirmation
  text-align: center
  margin: 20px

  &.footer

    button
      width: 100%
</style>
